import moment from "moment";
import { getDays } from "./rrule";

const WEEKDAY_ORDER = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

const isValidDay = (day) => WEEKDAY_ORDER.includes(day);

const normalizeDays = (days) => {
  if (!Array.isArray(days)) {
    return [];
  }

  return days
    .flatMap((day) => {
      if (typeof day !== "string") {
        return [];
      }

      return day.includes(",") ? day.split(",") : [day];
    })
    .map((day) => day.trim())
    .filter(isValidDay);
};

const sortDays = (days) => {
  return [...new Set(normalizeDays(days))].sort(
    (left, right) => WEEKDAY_ORDER.indexOf(left) - WEEKDAY_ORDER.indexOf(right)
  );
};

const isWeeklyRule = (rule) => typeof rule?.rrule === "string" && rule.rrule.includes("FREQ=WEEKLY");

const isRuleActive = (rule, referenceDate = moment()) => {
  if (!rule) {
    return false;
  }

  const dateFrom = rule?.date_from && rule.date_from !== "null" ? moment(rule.date_from) : null;
  const dateTo = rule?.date_to && rule.date_to !== "null" ? moment(rule.date_to) : null;

  if (dateFrom?.isAfter(referenceDate, "day")) {
    return false;
  }

  if (dateTo?.isBefore(referenceDate, "day")) {
    return false;
  }

  return true;
};

const getPartnerId = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return String(value);
};

const filterRulesByPartner = (rules, partnerId) => {
  const normalizedPartnerId = getPartnerId(partnerId);

  if (!normalizedPartnerId) {
    return rules;
  }

  return rules.filter((rule) => getPartnerId(rule?.partner_id) === normalizedPartnerId);
};

const formatDays = (days, t) => {
  return sortDays(days)
    .map((day) => t(`rrules.byweekday.${day}`))
    .join(", ");
};

const getRuleDays = (rules) => {
  return sortDays(
    rules.flatMap((rule) => {
      if (!rule?.rrule) {
        return [];
      }

      return getDays(rule.rrule);
    })
  );
};

export const getDisplayedCleaningDays = (element, options = {}) => {
  const rules = Array.isArray(element?.service_rrules) ? element.service_rrules : [];
  const referenceDate = options?.referenceDate ? moment(options.referenceDate) : moment();
  const partnerId =
    options?.partnerId ??
    options?.partner?.id ??
    options?.record?.partner?.id ??
    options?.record?.partner_id ??
    null;

  const activeRules = filterRulesByPartner(
    rules.filter((rule) => isWeeklyRule(rule) && isRuleActive(rule, referenceDate)),
    partnerId
  );

  const activeRuleDays = getRuleDays(activeRules);

  if (activeRuleDays.length) {
    return activeRuleDays;
  }

  const recordRuleDays = options?.record?.rrule ? getDays(options.record.rrule) : [];

  return sortDays(recordRuleDays.length ? recordRuleDays : options?.fallbackDays);
};

/**
 * Extract and format active cleaning days from a record
 * @param {Object} record - Unit record with schedule data
 * @param {Function} t - Translation function
 * @param {boolean} onlyActive - Get Active days only from Active rules
 * @returns {string|null} - Formatted active days string or null
 */
export const getTheActiveDays = (record, t, onlyActive = false) => {
  const element = record?.building_unit ?? record;
  const activeDays = getDisplayedCleaningDays(element, {
    partnerId: record?.partner?.id ?? record?.partner_id,
  });

  if (activeDays.length) {
    return formatDays(activeDays, t);
  }

  if (onlyActive) {
    return null;
  }

  const fallbackDays = record?.rrule
    ? getDays(record.rrule)
    : element?.cleaning_days;

  return fallbackDays?.length ? formatDays(fallbackDays, t) : null;
};

/**
 * Extract active cleaning days as array (without translation)
 * @param {Object} record - Unit record with schedule data
 * @returns {Array} - Array of day codes
 */
export const getActiveDaysArray = (record) => {
  const element = record?.building_unit ?? record;
  const activeDays = getDisplayedCleaningDays(element, {
    partnerId: record?.partner?.id ?? record?.partner_id,
  });

  if (activeDays.length) {
    return activeDays;
  }

  if (record?.rrule) {
    return sortDays(getDays(record.rrule));
  }

  return sortDays(element?.cleaning_days);
};


/**
 * Extract active cleaning days as formatted text
 * @returns {string} - Array of day codes
 * @param days
 * @param element
 * @param t
 */
export const showActiveDays = (days, element, t, options = {}) => {
  return formatDays(
    getDisplayedCleaningDays(element, {
      ...options,
      fallbackDays: days,
    }),
    t
  );
};
